import { useCallback, useEffect, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SectionCard, Field, Skeleton } from '../pipeline/ui';
import { formatCredits } from '../../lib/credits';

const EMPTY_BUNDLE = { id: null, label: '', credits: '', price_usd: '', price_irr: '', sort_order: 100, active: true };

// Full CRUD editor for the credit_bundles registry (super-admin). No hardcoded packs — rows come
// from list_bundles. Create / Edit / Deactivate-Restore, all thin passthroughs to the 031 RPCs.
export function BundleEditor({ bundles = [], onSave, onToggleActive }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_BUNDLE);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const edit = (b) => {
    setForm({
      id: b.id, label: b.label ?? '', credits: b.credits ?? '',
      price_usd: b.price_usd ?? '', price_irr: b.price_irr ?? '', sort_order: b.sort_order ?? 100, active: b.active !== false,
    });
    setEditing(true);
  };
  const reset = () => { setForm(EMPTY_BUNDLE); setEditing(false); };

  const submit = async () => {
    if (!String(form.label).trim()) { toast.error(t('bundleAdmin.labelRequired')); return; }
    setSaving(true);
    try { await onSave(form); toast.success(t('bundleAdmin.saved')); reset(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <SectionCard title={t('bundleAdmin.bundlesTitle')} testId="bundles-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="bundles-table">
          <thead>
            <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="text-start py-2 pe-4"><T k="bundleAdmin.colLabel" /></th>
              <th className="text-start py-2 pe-4"><T k="bundleAdmin.colCredits" /></th>
              <th className="text-start py-2 pe-4"><T k="bundleAdmin.colPrice" /></th>
              <th className="text-start py-2 pe-4"><T k="bundleAdmin.colSort" /></th>
              <th className="text-start py-2 pe-4"><T k="bundleAdmin.colActive" /></th>
              <th className="text-start py-2"></th>
            </tr>
          </thead>
          <tbody className="font-data">
            {bundles.length === 0 ? (
              <tr><td colSpan="6" className="py-3 text-muted-foreground" data-testid="bundles-empty">—</td></tr>
            ) : bundles.map((b) => (
              <tr key={b.id} className={`border-b border-border ${b.active === false ? 'opacity-50' : ''}`} data-testid={`bundle-row-${b.id}`}>
                <td className="py-2 pe-4 text-foreground">{b.label}</td>
                <td className="py-2 pe-4">{Number(b.credits).toLocaleString()}</td>
                <td className="py-2 pe-4">{b.price_irr != null ? `${Number(b.price_irr).toLocaleString()} ${t('wallet.rial')}` : '—'}</td>
                <td className="py-2 pe-4 text-muted-foreground">{b.sort_order}</td>
                <td className="py-2 pe-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${b.active === false ? 'bg-muted text-muted-foreground border-border' : 'bg-success-soft text-success border-success-border'}`} data-testid={`bundle-active-${b.id}`}>
                    {b.active === false ? t('bundleAdmin.inactive') : t('bundleAdmin.active')}
                  </span>
                </td>
                <td className="py-2 text-end whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => edit(b)} data-testid={`bundle-edit-${b.id}`}><T k="bundleAdmin.edit" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => onToggleActive(b.id, b.active === false)} data-testid={`bundle-toggle-active-${b.id}`}>
                    {b.active === false ? t('bundleAdmin.activate') : t('bundleAdmin.deactivate')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border pt-4 mt-2">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-semibold text-foreground" data-testid="bundle-form-title">{editing ? t('bundleAdmin.editBundle') : t('bundleAdmin.addBundle')}</h4>
          {editing && <Button variant="ghost" size="sm" onClick={reset} data-testid="bundle-cancel"><T k="bundleAdmin.cancel" /></Button>}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={<T k="bundleAdmin.colLabel" />}>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Starter" data-testid="bundle-label" />
          </Field>
          <Field label={<T k="bundleAdmin.colCredits" />}>
            <Input type="number" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} data-testid="bundle-credits" />
          </Field>
          <Field label={<T k="bundleAdmin.colPrice" />}>
            <Input type="number" step="1" value={form.price_irr} onChange={(e) => setForm({ ...form, price_irr: e.target.value })} data-testid="bundle-price-irr" />
          </Field>
          <Field label={<T k="bundleAdmin.colSort" />}>
            <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="bundle-sort" />
          </Field>
        </div>
        <div className="mt-4">
          <Button onClick={submit} disabled={saving} data-testid="bundle-save">
            {saving ? t('bundleAdmin.saving') : (editing ? t('bundleAdmin.updateBundle') : t('bundleAdmin.addBundle'))}
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

// Manual "grant bundle to user" (no gateway): email + bundle picker -> mark_bundle_purchased.
// fixedEmail pins the card to one known user (the Manage popup): the email field disappears
// and the target cannot be retyped. Without it the card behaves as before — by email.
export function PurchaseBundleCard({ bundles = [], peg = 0.01, onPurchase, fixedEmail }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [bundleId, setBundleId] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const activeBundles = bundles.filter((b) => b.active !== false);
  const target = (fixedEmail ?? email).trim();
  const canSubmit = !!target && !!bundleId;

  const submit = async () => {
    if (!canSubmit) return;
    setError(''); setResult(null); setBusy(true);
    try {
      const r = await onPurchase(target, bundleId);
      setResult(r); setEmail(''); setBundleId('');
      toast.success(t('bundleAdmin.purchased'));
    } catch (e) {
      if (e?.message === 'user_not_found' || e?.status === 404) {
        setError(e?.message === 'bundle_not_found' ? t('bundleAdmin.bundleNotFound') : t('bundleAdmin.purchaseNotFound'));
      } else toast.error(e.message);
    } finally { setBusy(false); }
  };

  return (
    <SectionCard title={t('bundleAdmin.purchaseTitle')} testId="bundle-purchase-card">
      <div className="flex flex-wrap items-end gap-3">
        {!fixedEmail && (
        <Field label={<T k="bundleAdmin.purchaseEmail" />}>
          <Input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            placeholder={t('bundleAdmin.purchaseEmailPlaceholder')}
            data-testid="purchase-email"
            className="w-72"
          />
        </Field>
        )}
        <Field label={<T k="bundleAdmin.purchaseBundle" />}>
          <select
            value={bundleId}
            onChange={(e) => setBundleId(e.target.value)}
            className="w-64 rounded-md border border-border px-3 py-2 text-sm bg-card"
            data-testid="purchase-bundle-select"
          >
            <option value=""><T k="bundleAdmin.purchaseSelect" /></option>
            {activeBundles.map((b) => (
              <option key={b.id} value={b.id}>{b.label} · {Number(b.credits).toLocaleString()} · {b.price_irr != null ? `${Number(b.price_irr).toLocaleString()} ${t('wallet.rial')}` : '—'}</option>
            ))}
          </select>
        </Field>
        <Button onClick={submit} disabled={busy || !canSubmit} data-testid="purchase-submit"><T k="bundleAdmin.purchaseDo" /></Button>
      </div>
      {error && <p className="mt-3 text-sm text-destructive" data-testid="purchase-error">{error}</p>}
      {result && (
        <p className="mt-3 text-sm text-success" data-testid="purchase-success">
          {t('bundleAdmin.purchasedTo')} <span className="font-data">{result.email}</span> · {formatCredits(result.wallet?.balance_credits, peg)}
        </p>
      )}
    </SectionCard>
  );
}

// Container: fetches bundles + wires the CRUD/purchase passthroughs.
export default function BundleAdmin() {
  const [bundles, setBundles] = useState(null);
  const [peg, setPeg] = useState(0.01);

  const load = useCallback(() => {
    api.tenantAdmin('list_bundles').then((r) => setBundles(r.bundles || [])).catch((e) => toast.error(e.message));
    api.tenantAdmin('get_wallet_config').then((r) => { if (r.config?.credit_usd_peg) setPeg(Number(r.config.credit_usd_peg)); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async (form) => {
    await api.tenantAdmin('upsert_bundle', {
      id: form.id || undefined,
      label: form.label,
      credits: Number(form.credits),
      price_usd: Number(form.price_usd) || 0,
      price_irr: form.price_irr,
      active: form.active,
      sort_order: Number(form.sort_order),
    });
    load();
  };
  const onToggleActive = async (id, active) => {
    try { await api.tenantAdmin('set_bundle_active', { id, active }); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (bundles === null) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="flex flex-col gap-6">
      <BundleEditor bundles={bundles} onSave={onSave} onToggleActive={onToggleActive} />
      {/* Granting a bundle to a user moved into the per-user Manage popup (Ali, 2026-08-03). */}
    </div>
  );
}
